import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CircularProgress, Snackbar, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../../API/api';
import { AppContext } from '../../App/AppContext';
import { ConfigContext } from '../../App/App';
import { DEFAULT_FORM_HEADER_1, DEFAULT_FORM_HEADER_2 } from '../../../config/configType';
import DynamicFormField from './DynamicFormFields/DynamicFormField';

/* ------------------------------------------------------------------
   Types / Interfaces
-------------------------------------------------------------------*/
interface FormField {
  Label: string;
  Description: string;
  DataType: string;
  AllowedValues: string[];
  Required: boolean;
  FieldId: string;
  Validation: string;
  Children?: ChildGroup[]; // unlimited depth
}

// Define a type for each "group" in Children
// (If you haven't declared it globally.)
interface ChildGroup {
  show_on_value: string | string[];
  fields: FormField[];
}

/* ------------------------------------------------------------------
   Helpers
-------------------------------------------------------------------*/
// const isEmpty = (v: any) =>
//   v === null ||
//   v === undefined ||
//   (typeof v === 'string' && v.trim() === '') ||
//   (Array.isArray(v) && v.length === 0);

/** Recursively convert a single JSON field definition → FormField */
function parseSingleField(rawField: any): FormField {
  const meta = rawField.metadata?.[0] ?? {};

  // ---- Convert groups (new or old style) ----
  let children: ChildGroup[] | undefined;

  // NEW style: Children is an array
  if (Array.isArray(rawField.Children)) {
    children = rawField.Children.map((grp: any) => {
      const rawFields = grp.fields || {};
      const parsedKids = Object.values(rawFields).map(parseSingleField);
      return {
        show_on_value: grp.show_on_value,
        fields: parsedKids,
      };
    });
  }
  // OLD style: single children object + children_show_on_value
  else if (rawField.children) {
    const triggerVal = rawField.children_show_on_value ?? '';
    const rawKidsObj = rawField.children;
    const parsedKids = Object.values(rawKidsObj).map(parseSingleField);
    children = [
      {
        show_on_value: triggerVal,
        fields: parsedKids,
      },
    ];
  }

  return {
    Label: rawField.title,
    Description: rawField.description,
    DataType: meta.data_type,
    Required: meta.required,
    AllowedValues: meta.allowed_values,
    Validation: meta.validation,
    FieldId: meta.field_id,
    Children: children,
  };
}

/** Extract the top‑level EntranceForm → FormField[] (with unlimited depth) */
function parseFormFieldsFromConfig(cfg: any): FormField[] {
  if (!cfg?.EntranceForm?.form_details?.properties) return [];

  const props = cfg.EntranceForm.form_details.properties;
  const topLevelFields = Object.values(props).map(parseSingleField);

  return topLevelFields;
}

/** Collect *all* descendant FieldIds (including the given field) */
function gatherIdsRecursive(f: FormField): string[] {
  const ids = [f.FieldId];
  if (f.Children) {
    f.Children.forEach((grp) => {
      grp.fields.forEach((kid) => ids.push(...gatherIdsRecursive(kid)));
    });
  }
  return ids;
}

/* ------------------------------------------------------------------
   Component
-------------------------------------------------------------------*/
const DynamicForm: React.FC = React.memo(() => {
  const { state, dispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const formHeader1 = config.FormHeader1 || DEFAULT_FORM_HEADER_1;
  const formHeader2 = config.FormHeader2 || DEFAULT_FORM_HEADER_2;
  const formDisclosure = config.FormDisclosure;

  // ------------- derive form fields (memo) -------------
  const formFields: FormField[] = useMemo(() => parseFormFieldsFromConfig(config), [config]);

  // Find all fields with DataType "Address" to determine if we need Google Maps (memoized)
  const addressFields = useMemo(() => formFields.filter(f => f.DataType === 'Address'), [formFields]);
  const hasAddressFields = addressFields.length > 0;

  // Filter form fields for display - show all fields normally, but group address fields (memoized)
  const filteredFormFields = useMemo(() => {
    if (!hasAddressFields) {
      return formFields; // No address fields, show everything normally
    }

    // If we have address fields, replace them with a single "Address Autocomplete" field
    const nonAddressFields = formFields.filter(f => f.DataType !== 'Address');

    // Create a virtual address field for the autocomplete
    const addressAutocompleteField: FormField = {
      Label: 'Address',
      Description: '',
      DataType: 'AddressAutocomplete', // Special type for our autocomplete
      Required: addressFields.some(f => f.Required), // Required if any address field is required
      AllowedValues: [],
      Validation: '',
      FieldId: '__address_autocomplete__',
      Children: undefined
    };

    return [addressAutocompleteField, ...nonAddressFields];
  }, [formFields, hasAddressFields, addressFields]);

  /** Build an *initial* flat validation skeleton for every field in the tree */
  const buildInitialValidations = useCallback(() => {
    const rec: any[] = [];
    const walk = (f: FormField) => {
      rec.push({
        FieldName: f.Label,
        FieldId: f.FieldId,
        IsValid: !f.Required,
        Error: '',
        FieldValue: '',
      });
      f.Children?.forEach((grp) => grp.fields.forEach(walk));
    };
    formFields.forEach(walk);
    return rec;
  }, [formFields]);

  const [fieldValidations, setFieldValidations] = useState<any[]>(buildInitialValidations);
  // hold previously entered values even when fields are hidden
  const [, setFieldValues] = useState<Record<string, any>>({});
  const [formValid, setFormValid] = useState(() => fieldValidations.every((v) => v.IsValid));
  // Reset field values if fields change

  // Reset skeleton if fields change
  useEffect(() => {
    const next = buildInitialValidations();
    setFieldValidations(next);
    setFormValid(next.every((v) => v.IsValid));
  }, [buildInitialValidations]);

  /** --------------------------------------------------
   *  validateField – central callback used by each field
   * --------------------------------------------------*/
  const validateField = useCallback(
    (fieldId: string, label: string, isValid: boolean, error: string, value: any) => {

      /* ----------------------------------------------------
         Recursive helpers to find & traverse trees
      ----------------------------------------------------*/
      const findFieldPath = (id: string, nodes: FormField[]): FormField[] | null => {
        for (const node of nodes) {
          if (node.FieldId === id) return [node];
          if (node.Children) {
            for (const grp of node.Children) {
              const found = findFieldPath(id, grp.fields);
              if (found) return [node, ...found];
            }
          }
        }
        return null;
      };
      setFieldValues((prevVals) => {
        const newVals = { ...prevVals, [fieldId]: value };

        setFieldValidations((prevValids) => {
          let next = [...prevValids];

          // locate entire path (parent → … → target) in the tree
          const path = findFieldPath(fieldId, formFields);
          if (!path) return next; // should not happen

          const targetField = path[path.length - 1];

          // -------- 1) upsert validation record for the target --------
          const idx = next.findIndex((r) => r.FieldId === targetField.FieldId);
          const rec = {
            FieldName: label,
            FieldId: targetField.FieldId,
            IsValid: isValid,
            Error: error,
            FieldValue: value,
          };
          if (idx === -1) next.push(rec); else next[idx] = rec;

          // -------- 2) handle its descendants visibility --------
          if (targetField.Children) {
            // depending on value, some groups are triggered, some not
            const visibleGroups = targetField.Children.filter((g) =>
              Array.isArray(g.show_on_value)
                ? g.show_on_value.includes(value)
                : g.show_on_value === value
            );

            const visibleIds = visibleGroups.flatMap((g) =>
              g.fields.flatMap(gatherIdsRecursive)
            );
            const allDescIds = targetField.Children.flatMap((g) =>
              g.fields.flatMap(gatherIdsRecursive)
            );

            // Remove hidden subtree records
            next = next.filter((v) => !allDescIds.includes(v.FieldId) || visibleIds.includes(v.FieldId));

            // Add missing visible subtree records
            const addFieldTree = (f: FormField) => {
              const id = f.FieldId;
              if (!next.find((r) => r.FieldId === id)) {
                next.push({
                  FieldName: f.Label,
                  FieldId: id,
                  IsValid: !f.Required,
                  Error: '',
                  FieldValue: newVals[id] ?? '',
                });
              }
              f.Children?.forEach((grp) => grp.fields.forEach(addFieldTree));
            };
            visibleGroups.forEach((grp) => grp.fields.forEach(addFieldTree));
          }

          // -------- 3) recalculate overall validity --------
          setFormValid(next.every((v) => v.IsValid));
          return next;
        });
        return newVals;
      });
    },
    [formFields]
  );

  /* ----------------------------------------------------
     Submission logic
  ----------------------------------------------------*/
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const submitForm = async () => {
    if (!formValid) return;
    setSubmitting(true);
    const request = {
      indexname: config.IndexName,
      sessionid: state.sessionid,
      objectid: config?.EntranceForm?.form_details?.object_id ?? 'missing-object-id',
      fields: fieldValidations.map((v) => ({ fieldid: v.FieldId, fieldvalue: v.FieldValue })),
    };
    try {
      await api.submitDyanmicInformation.submitDyanmicInformation(request);
      dispatch({ type: 'setEnteredInformation', hasEnteredInformation: true });
      navigate('/Home');
    } catch (err) {
      console.error(err);
      setSnackOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  /* Auto‑skip if no EntranceForm */
  useEffect(() => {
    if (formFields.length === 0) {
      dispatch({ type: 'setEnteredInformation', hasEnteredInformation: true });
      navigate('/Home', { replace: true });
    }
  }, [formFields.length, dispatch, navigate]);

  if (formFields.length === 0) return null;

  /* ------------------------------------------------------------------
     Render
  -------------------------------------------------------------------*/
  return (
    <Box component="main" id="main" sx={{ flex: 1 }}>
      <Card sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {submitting && (
          <Card role="status" aria-live="polite" sx={{
            background: 'white', opacity: 0.5, width: '100%', height: '100%', position: 'absolute',
            top: 0, left: 0, zIndex: 99, display: 'flex', justifyContent: 'center', alignItems: 'center',
          }}>
            <CircularProgress />
          </Card>
        )}

        <form className="welcomeSubmit" aria-busy={submitting}>
          <Box sx={{ mb: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography component="h1" sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>{formHeader1}</Typography>
            <Typography component="h2" sx={{ fontSize: '1rem' }}>{formHeader2}</Typography>
            {formDisclosure && (
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: '0px' }} >
                {formDisclosure}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {filteredFormFields.map((ff) => (
              <DynamicFormField
                key={`${ff.FieldId}-stable`} // Stable key to prevent recreation
                form_field={ff}
                validateField={validateField}
                isChild={false}
                addressFields={addressFields} // Pass the address fields for mapping
              />
            ))}
          </Box>

          <Button
            variant="outlined"
            type="button"
            onClick={submitForm}
            disabled={!formValid || submitting}
            sx={{
              width: '100%', mt: 2,
              background: formValid ? `${config.PrimaryColor} !important` : '#e8e8e8 !important',
              color: formValid ? 'white !important' : 'gray !important',
              border: '0 !important',
            }}
            className="submitDynamicFormButton"
          >
            Get&nbsp;Started
          </Button>
        </form>
      </Card>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message="Something went wrong. Please try again."
      />
    </Box>
  );
});

export default DynamicForm;
